import React, { useState } from 'react';
import MeetingHeader from './components/MeetingHeader';
import AvatarStage from './components/AvatarStage';
import MeetingControls from './components/MeetingControls';
import CaptionsDock from './components/CaptionsDock';
import { FiCheck, FiCircle, FiMinus } from 'react-icons/fi';

/**
 * The Google-Meet-style interview room shown to the candidate (eventually via a shared link).
 * Full-bleed: header · stage(+controls) · captions. Captions are a right column on desktop and a
 * bottom panel on mobile (AI on top, chat+voice below), toggled from the control bar. The
 * avatar/screen logic lives in AvatarStage.
 */
const MeetingRoom = ({
  subtitle, elapsed, total, connection = 'connected', topicProgress = null,
  aiState = 'idle', level = 0, stateLabel, mutedSpeechVisible = false, localSpeechMonitoring = false,
  messages = [], inputActive = false,
  camStream, cameraOn, muted,
  sharing, screenStream, hideMirror,
  onToggleMute, onToggleCamera, onToggleShare, onToggleHideMirror, onEnd,
  typed, setTyped, onSendTyped, sendDisabled,
  closingSeconds, disabled,
  audioWarning, audioWarningHidden, audioWarningLabel, onShowAudioWarning, onRetryAudio, onUseTyping,
  onDoneSpeaking, canFinishSpeaking,
  recoveryActionLabel, recoveryBusy, recoveryCanType,
}) => {
  // Default open: a right column on desktop, a bottom panel on mobile (AI on top, chat+voice below).
  const [captionsOpen, setCaptionsOpen] = useState(true);

  const dock = (
    <CaptionsDock
      messages={messages}
      inputActive={inputActive}
      typed={typed}
      setTyped={setTyped}
      onSendTyped={onSendTyped}
      sendDisabled={sendDisabled}
      onDoneSpeaking={onDoneSpeaking}
      canFinishSpeaking={canFinishSpeaking && !disabled}
      onClose={() => setCaptionsOpen(false)}
    />
  );

  const topicItems = topicProgress?.items || [];
  const activeTopic = topicItems.find((item) => item.status === 'active');
  // Older status responses may still contain internal assessment outcomes.
  const discussedTopics = topicItems.filter((item) => ['discussed', 'completed', 'incomplete'].includes(item.status));
  const passedTopics = topicItems.filter((item) => ['passed', 'skipped'].includes(item.status));
  const discussedCount = topicProgress?.discussed ?? discussedTopics.length;
  const futureCount = topicItems.filter((item) => item.status === 'pending').length;
  const progressTitle = topicProgress?.stage || activeTopic?.label || 'Interview progress';
  const topicList = (
    <div className="space-y-3 text-xs">
      <div className="rounded bg-emerald-50 px-2.5 py-2 text-emerald-900 dark:bg-emerald-400/10 dark:text-emerald-100">
        <p className="text-[10px] font-semibold uppercase">{topicProgress?.stage ? 'Current stage' : 'Current topic'}</p>
        <p aria-live="polite" className="mt-1 break-words font-semibold leading-4">{progressTitle}</p>
      </div>
      {discussedTopics.length > 0 && <div>
        <p className="mb-1 font-semibold text-slate-600 dark:text-slate-300">Previously discussed</p>
        <ol className="space-y-1">
          {discussedTopics.map((item, index) => <li key={`discussed-${index}`} className="flex gap-2 py-0.5 text-slate-500 dark:text-slate-400"><FiCheck className="mt-0.5 shrink-0" aria-hidden="true" /><span className="break-words line-through">{item.label}</span></li>)}
        </ol>
      </div>}
      {passedTopics.length > 0 && <div>
        <p className="mb-1 font-semibold text-slate-600 dark:text-slate-300">Passed over</p>
        <ol className="space-y-1">
          {passedTopics.map((item, index) => <li key={`passed-${index}`} className="flex gap-2 py-0.5 text-slate-500 dark:text-slate-400"><FiMinus className="mt-0.5 shrink-0" aria-hidden="true" /><span className="break-words">{item.label}</span></li>)}
        </ol>
      </div>}
      {futureCount > 0 && <p className="flex items-center gap-2 text-slate-500 dark:text-slate-400"><FiCircle aria-hidden="true" />{futureCount} {futureCount === 1 ? 'topic' : 'topics'} ahead</p>}
    </div>
  );

  return (
    <div className="flex h-[100dvh] w-full flex-col overflow-hidden bg-slate-100 text-slate-900 dark:bg-[#0a0a12] dark:text-white">
      <div className="px-3 py-3 sm:px-5 sm:py-4">
        <MeetingHeader subtitle={subtitle} elapsed={elapsed} total={total} connection={connection}
          audioWarning={audioWarning} audioWarningHidden={audioWarningHidden}
          audioWarningLabel={audioWarningLabel}
          onShowAudioWarning={onShowAudioWarning} onRetryAudio={onRetryAudio} onUseTyping={onUseTyping}
          recoveryActionLabel={recoveryActionLabel} recoveryBusy={recoveryBusy} recoveryCanType={recoveryCanType} />
      </div>

      {/* stacks on mobile (AI on top, chat+voice below), side-by-side on desktop */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 pb-3 sm:px-5 sm:pb-5 lg:flex-row lg:overflow-hidden">
        {topicProgress?.total > 0 && (
          <>
            <details className="shrink-0 rounded-md border border-slate-200 bg-white px-3 py-2 text-slate-700 dark:border-white/10 dark:bg-[#171722] dark:text-slate-200 lg:hidden">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-xs font-semibold">
                <span className="min-w-0 truncate">{progressTitle}</span>
                <span className="shrink-0">{discussedCount} of {topicProgress.total} discussed</span>
              </summary>
              <div className="mt-2 max-h-48 overflow-y-auto border-t border-slate-200 pt-2 dark:border-white/10">{topicList}</div>
            </details>
            <aside aria-label="Interview topic progress" className="hidden shrink-0 overflow-y-auto rounded-md border border-slate-200 bg-white p-3 text-slate-700 dark:border-white/10 dark:bg-[#171722] dark:text-slate-200 lg:block lg:w-[220px]">
              <div className="mb-2 flex items-center justify-between gap-2 text-xs font-semibold">
                <span>Topics</span><span>{discussedCount} of {topicProgress.total} discussed</span>
              </div>
              {topicList}
            </aside>
          </>
        )}
        {/* stage column (+ floating controls) — height-capped on mobile so the dock is visible below */}
        <div className={`relative flex min-w-0 flex-1 flex-col lg:min-h-0 ${topicProgress?.total ? 'min-h-[32vh]' : 'min-h-[44vh]'}`}>
          <div className="min-h-0 flex-1">
            <AvatarStage
              aiState={aiState} level={level} stateLabel={stateLabel}
              sharing={sharing} screenStream={screenStream} hideMirror={hideMirror}
              camStream={camStream} cameraOn={cameraOn} muted={muted}
            />
          </div>

          {closingSeconds != null && (
            <div className="pointer-events-none absolute inset-x-0 top-4 flex justify-center">
              <div className="rounded-full bg-indigo-100 px-4 py-1.5 text-sm text-indigo-700 ring-1 ring-indigo-300 backdrop-blur dark:bg-indigo-500/20 dark:text-indigo-200 dark:ring-indigo-400/30">
                Wrapping up — preparing your summary… ({closingSeconds}s)
              </div>
            </div>
          )}

          <div className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center px-3">
            <div className="pointer-events-auto relative flex max-w-full flex-col items-center gap-2">
              {mutedSpeechVisible && <div role="status" aria-live="polite"
                className="absolute bottom-full mb-3 w-max max-w-[min(320px,calc(100vw-48px))] rounded-md bg-slate-900/80 px-3 py-2 text-center text-xs leading-5 text-white shadow-md backdrop-blur-sm">
                If you're speaking, turn on your microphone to answer.
              </div>}
              <MeetingControls
                muted={muted} onToggleMute={onToggleMute} localSpeechMonitoring={localSpeechMonitoring}
                cameraOn={cameraOn} onToggleCamera={onToggleCamera}
                sharing={sharing} onToggleShare={onToggleShare}
                hideMirror={hideMirror} onToggleHideMirror={onToggleHideMirror}
                captionsOpen={captionsOpen} onToggleCaptions={() => setCaptionsOpen((v) => !v)}
                onEnd={onEnd} disabled={disabled}
              />
            </div>
          </div>
        </div>

        {/* captions: a bottom panel on mobile (chat+voice below the AI), a right column on desktop */}
        {captionsOpen && (
          <div className={`min-h-[320px] shrink-0 overflow-hidden lg:min-h-0 lg:w-[340px] lg:basis-auto xl:w-[380px] ${topicProgress?.total ? 'basis-[36vh]' : 'basis-[40vh]'}`}>
            {dock}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingRoom;
